import { useEffect, useState } from 'react'
import { tripo3d_loadModel } from '../sdk/tripo3d'
import { sleep } from '@beenotung/tslib/async/wait'

export default function ObjectViewer({
  object_id,
  onPromptReceived,
  isDisplayMode = false,
  fallback,
}: {
  object_id: number
  onPromptReceived?: (prompt: string) => void
  isDisplayMode?: boolean
  fallback?: React.ReactNode
}) {
  type objectType = {
    model?: string
    preview?: string
    render?: string
  }

  const [obj, setObj] = useState<objectType>({})
  const [status, setStatus] = useState<string>('pending')
  const [progress, setProgress] = useState<number>(0)
  const [prompt, setPrompt] = useState<string>('')
  const [byUser, setByUser] = useState<string>('')

  // Load model-viewer script
  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'module'
    script.src =
      'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js'
    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  const loadModel = async (object_id: number) => {
    await sleep(2000)
    try {
      const res = await tripo3d_loadModel({
        params: { object_id: `${object_id}` },
      })

      setStatus(prev => {
        if (res.status != prev) return res.status
        return prev
      })
      setProgress(prev => {
        if (res.progress != null && res.progress != prev) return res.progress
        return prev
      })

      if (res.prompt) {
        setPrompt(res.prompt)
      }

      if (res.user) {
        setByUser(res.user)
      }

      // Call the callback with prompt if available
      if (res.prompt && onPromptReceived) {
        onPromptReceived(res.prompt)
      }

      if (res.status === 'success' && res.urls) {
        setObj({
          model: res.urls.model,
          preview: res.urls.preview,
          render: res.urls.render,
        })
      } else if (res.status === 'pending') {
        if (res.urls && res.urls.preview) {
          setObj(prev => {
            if (res.urls && prev.preview != res.urls.preview)
              return { preview: res.urls.preview }
            return prev
          })
        }
        setTimeout(() => loadModel(object_id), 3000)
      } else if (res.status === 'fail') {
        console.log(`fail to load model with object_id ${object_id}`)
      }
      // Note: Any other status (like 'failed', 'error', etc.) will stop polling
    } catch (error) {
      console.error('Error loading model:', error)
    }
  }

  useEffect(() => {
    loadModel(object_id)
  }, [object_id])

  if (obj.model) {
    return (
      <div className="flex-col">
        {isDisplayMode && <div>By: {byUser}</div>}
        <model-viewer
          src={obj.model}
          ar
          poster={obj.preview || obj.render || ''}
          shadow-intensity="1"
          camera-controls
          touch-action="pan-y"
        />
        {isDisplayMode && <div className="prompt-card">{prompt}</div>}
      </div>
    )
  }

  if (status == 'pending') {
    return (
      <div className="model-viewer">
        {obj.preview && (
          <div
            className="model-viewer-background"
            style={{ backgroundImage: `url(${obj.preview})` }}
          />
        )}
        <div className="model-viewer-overlay">
          <span>Generating your model...</span>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="progress-text">{Math.round(progress)}%</span>
        </div>
      </div>
    )
  }

  // e.g. banned
  return <div className="model-viewer">debug: {status}</div>
}
